import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { serviceChargeApi } from '../lib/api';
import { toast } from 'sonner';
import {
  HandCoins,
  TrendUp,
  Lightning,
  Receipt,
  Warning,
  CheckCircle,
  CaretDown,
  CaretUp,
  Users,
  ChartLineUp,
} from '@phosphor-icons/react';
import {
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import DateRangeFilter from '../components/DateRangeFilter';
import { resolveInitialRange, isValidISO, fmtDDMMYYYY } from '../lib/dateRange';

// ─── Format helpers ────────────────────────────────────────────────────────
const fmtCurrency = (v) => {
  if (v == null) return '0';
  return Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};
const fmtShort = (v) => {
  if (v == null) return '0';
  const n = Number(v);
  if (Math.abs(n) >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toFixed(0);
};
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(v % 1 === 0 ? 0 : 1)}%`);
const fmtTime = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return String(ts).slice(11, 16);
    // Local time display — see 1OS Date Policy in src/lib/date.js.
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch { return ''; }
};

// ─── KPI Card (top-of-module) ──────────────────────────────────────────────
const KpiCard = ({ label, value, subtext, accentColor, testId }) => (
  <div
    className="relative rounded-2xl bg-[#0F1115] border border-white/5 p-5 overflow-hidden"
    style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}
    data-testid={testId}
  >
    {/* 2px top accent bar */}
    <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ backgroundColor: accentColor }} />
    <p className="text-[11px] uppercase tracking-[0.08em] text-zinc-500 font-medium">{label}</p>
    <p className="text-[28px] font-semibold font-mono mt-2 text-zinc-100 leading-none">{value}</p>
    {subtext && <p className="text-[11px] text-zinc-500 mt-2 font-mono">{subtext}</p>}
  </div>
);

// ─── Insight bullet ────────────────────────────────────────────────────────
const InsightBullet = ({ icon: Icon, text, accent = 'text-amber-400' }) => (
  <div className="flex items-start gap-3 py-2">
    <Icon size={14} className={`${accent} mt-0.5 shrink-0`} weight="duotone" />
    <p className="text-[13px] text-zinc-300 leading-relaxed">{text}</p>
  </div>
);

// ─── Reason-type pill ──────────────────────────────────────────────────────
const ReasonPill = ({ type }) => {
  const map = {
    print_time_waiver: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', label: 'Waiver' },
    inferred_removal: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', label: 'Removal' },
    both: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', label: 'Both' },
  };
  const s = map[type] || { bg: 'bg-zinc-500/10', border: 'border-zinc-500/30', text: 'text-zinc-400', label: type || '—' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-mono border ${s.bg} ${s.border} ${s.text}`}>
      {s.label}
    </span>
  );
};

// ─── Leaderboard row ───────────────────────────────────────────────────────
const LeaderRow = ({ row, maxRate, isTop }) => {
  const barWidth = Math.max(3, Math.round((row.waiver_rate_pct / Math.max(1, maxRate)) * 100));
  return (
    <tr className="border-t border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`leaderboard-row-${row.server_name}`}>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="text-[13px] text-zinc-200 truncate max-w-[200px]">{row.server_name}</span>
          {isTop && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[9px] font-mono text-amber-400" title="Highest waiver rate">
              <Warning size={9} weight="fill" /> Top
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-3 text-right text-[13px] text-zinc-400 font-mono">{row.total_bills}</td>
      <td className="px-3 py-3 text-right text-[13px] text-zinc-200 font-mono">{row.waived_bills}</td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2 justify-end">
          <div className="w-20 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-amber-400" style={{ width: `${barWidth}%` }} />
          </div>
          <span className="text-[13px] text-amber-300 font-mono w-12 text-right">{fmtPct(row.waiver_rate_pct)}</span>
        </div>
      </td>
      <td className="px-3 py-3 text-right text-[13px] text-zinc-200 font-mono">₹{fmtCurrency(row.total_waived)}</td>
      <td className="px-5 py-3 text-right text-[13px] text-zinc-400 font-mono">₹{fmtCurrency(row.avg_waived)}</td>
    </tr>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────
const ServiceChargePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // URL-driven date range — shared contract with Sales Overview /
  // Sales Explorer / Discounts. Default = This Month when missing.
  const initial = resolveInitialRange(searchParams);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo,   setDateTo]   = useState(initial.to);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditSort, setAuditSort] = useState({ key: 'timestamp', dir: 'desc' });
  const [reasonFilter, setReasonFilter] = useState('all');

  // Sync URL whenever the applied range changes.
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    sp.set('date_from', dateFrom);
    sp.set('date_to', dateTo);
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const handleRangeChange = (from, to) => {
    if (!isValidISO(from) || !isValidISO(to)) {
      toast.error('Invalid dates'); return;
    }
    setDateFrom(from); setDateTo(to);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { audit_limit: 200 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await serviceChargeApi.getSummary(params);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load service-charge data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  const filteredAudit = useMemo(() => {
    if (!data?.audit_rows) return [];
    let rows = reasonFilter === 'all'
      ? data.audit_rows
      : data.audit_rows.filter((r) => r.reason_type === reasonFilter);
    const dir = auditSort.dir === 'asc' ? 1 : -1;
    const key = auditSort.key;
    return [...rows].sort((a, b) => {
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;
      if (av === bv) return 0;
      return av > bv ? dir : -dir;
    });
  }, [data, reasonFilter, auditSort]);

  const toggleSort = (key) => setAuditSort((s) => (
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
  ));

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Loading service-charge insights…</div>
      </div>
    );
  }

  const k = data?.kpis || {};
  const trend = data?.daily_trend || [];
  const leaderboard = data?.leaderboard || [];
  const insights = data?.insights || [];
  const maxLeaderRate = leaderboard.reduce((m, r) => Math.max(m, r.waiver_rate_pct), 0);

  return (
    <div className="space-y-6 px-1 pb-8" data-testid="service-charge-page">
      {/* Page Title */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <HandCoins size={22} className="text-amber-400" weight="duotone" />
          </div>
          <div>
            <h1 className="text-[22px] font-semibold font-['Outfit'] tracking-tight text-zinc-100">Service Charge</h1>
            <p className="text-[12px] text-zinc-500 mt-0.5" data-testid="sc-subtitle">
              Revenue control — collected, waived, removed, and who's responsible · {fmtDDMMYYYY(dateFrom)} → {fmtDDMMYYYY(dateTo)}
            </p>
          </div>
        </div>
        <DateRangeFilter
          value={{ from: dateFrom, to: dateTo }}
          onChange={handleRangeChange}
          testIdPrefix="sc-range"
        />
      </div>

      {/* ROW 1 — KPI STRIP */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="sc-kpi-strip">
        <KpiCard
          label="Collected"
          value={`₹${fmtShort(k.collected)}`}
          subtext={`realised on invoice`}
          accentColor="#22C55E"
          testId="sc-kpi-collected"
        />
        <KpiCard
          label="Waived"
          value={`₹${fmtShort(k.waived)}`}
          subtext={`${k.waived_bills || 0} bill${k.waived_bills === 1 ? '' : 's'} · print-time`}
          accentColor="#F59E0B"
          testId="sc-kpi-waived"
        />
        <KpiCard
          label="Removed"
          value={`₹${fmtShort(k.removed)}`}
          subtext={`${k.removed_bills || 0} bill${k.removed_bills === 1 ? '' : 's'} · post-payment`}
          accentColor="#EF4444"
          testId="sc-kpi-removed"
        />
        <KpiCard
          label="Waiver Rate"
          value={fmtPct(k.waiver_rate_pct)}
          subtext={`of ${k.eligible_bills || 0} eligible bills`}
          accentColor="#F59E0B"
          testId="sc-kpi-waiver-rate"
        />
        <KpiCard
          label="Realization %"
          value={fmtPct(k.realization_pct)}
          subtext={`of ₹${fmtShort(k.expected)} expected`}
          accentColor="#3B82F6"
          testId="sc-kpi-realization"
        />
      </div>

      {/* ROW 2 — TREND + INSIGHTS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Trend chart (8 cols) */}
        <div className="lg:col-span-8 rounded-2xl bg-[#0F1115] border border-white/5 p-5" data-testid="sc-trend-card" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ChartLineUp size={14} className="text-blue-400" />
              <h2 className="text-[13px] uppercase tracking-[0.08em] text-zinc-400 font-medium">Service Charge Flow</h2>
            </div>
            <span className="text-[11px] text-zinc-500 font-mono">{trend.length} day{trend.length === 1 ? '' : 's'}</span>
          </div>
          {trend.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-[13px] text-zinc-500">No service-charge data for this range</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="gradCollected" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22C55E" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#22C55E" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradExpected" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.15} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                  <XAxis dataKey="date" stroke="#71717A" fontSize={10} tickFormatter={(v) => (v || '').slice(5)} />
                  <YAxis stroke="#71717A" fontSize={10} tickFormatter={(v) => fmtShort(v)} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0F1115', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 11 }}
                    labelStyle={{ color: '#E8EAF0', fontWeight: 500 }}
                    formatter={(value, name) => [`₹${fmtCurrency(value)}`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                  <Area type="monotone" dataKey="expected" stroke="#3B82F6" strokeWidth={1.2} fill="url(#gradExpected)" name="Expected" />
                  <Area type="monotone" dataKey="collected" stroke="#22C55E" strokeWidth={1.8} fill="url(#gradCollected)" name="Collected" />
                  <Bar dataKey="waived" fill="#F59E0B" fillOpacity={0.75} barSize={10} name="Waived" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="removed" fill="#EF4444" fillOpacity={0.75} barSize={10} name="Removed" radius={[2, 2, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Insights (4 cols) */}
        <div className="lg:col-span-4 rounded-2xl bg-[#0F1115] border border-white/5 p-5" data-testid="sc-insights-card" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
          <div className="flex items-center gap-2 mb-3">
            <Lightning size={14} className="text-amber-400" weight="duotone" />
            <h2 className="text-[13px] uppercase tracking-[0.08em] text-zinc-400 font-medium">Insights</h2>
          </div>
          <div className="space-y-1">
            {insights.map((text, i) => {
              const isRisk = /peak|accounts for|waived/i.test(text);
              const Icon = isRisk ? Warning : CheckCircle;
              return <InsightBullet key={i} icon={Icon} text={text} accent={isRisk ? 'text-amber-400' : 'text-blue-400'} />;
            })}
          </div>
        </div>
      </div>

      {/* ROW 3 — SERVER LEADERBOARD */}
      <div className="rounded-2xl bg-[#0F1115] border border-white/5 overflow-hidden" data-testid="sc-leaderboard" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-amber-400" />
            <h2 className="text-[13px] uppercase tracking-[0.08em] text-zinc-400 font-medium">Server Leaderboard</h2>
          </div>
          <span className="text-[11px] text-zinc-500 font-mono">min 5 bills · inhouse dine-in only · sorted by waiver rate</span>
        </div>
        {leaderboard.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-zinc-500" data-testid="sc-leaderboard-empty">
            No servers meet the minimum 5-bill threshold yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="sc-leaderboard-table">
              <thead className="bg-black/30 text-zinc-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-2.5">Server</th>
                  <th className="text-right px-3 py-2.5">Bills</th>
                  <th className="text-right px-3 py-2.5">Waived</th>
                  <th className="text-right px-3 py-2.5">Rate</th>
                  <th className="text-right px-3 py-2.5">Total ₹</th>
                  <th className="text-right px-5 py-2.5">Avg ₹</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((row, i) => (
                  <LeaderRow key={row.server_name} row={row} maxRate={maxLeaderRate} isTop={i === 0} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ROW 4 — AUDIT TABLE (collapsible) */}
      <div className="rounded-2xl bg-[#0F1115] border border-white/5 overflow-hidden" data-testid="sc-audit" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
        <button
          className="w-full px-5 py-4 border-b border-white/5 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
          onClick={() => setAuditOpen((v) => !v)}
          data-testid="sc-audit-toggle"
        >
          <div className="flex items-center gap-2">
            <Receipt size={14} className="text-amber-400" />
            <h2 className="text-[13px] uppercase tracking-[0.08em] text-zinc-400 font-medium">
              Audit Log · {filteredAudit.length} event{filteredAudit.length === 1 ? '' : 's'}
            </h2>
          </div>
          <span className="text-[11px] text-zinc-500 font-mono">{auditOpen ? 'Hide' : 'Show'}</span>
        </button>
        {auditOpen && (
          <>
            {/* Filters */}
            <div className="px-5 py-3 border-b border-white/5 bg-black/20 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Filter</span>
              {['all', 'print_time_waiver', 'inferred_removal'].map((rt) => (
                <button
                  key={rt}
                  onClick={() => setReasonFilter(rt)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-mono border transition-colors ${
                    reasonFilter === rt
                      ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                      : 'bg-transparent border-white/10 text-zinc-400 hover:border-white/20'
                  }`}
                  data-testid={`sc-audit-filter-${rt}`}
                >
                  {rt === 'all' ? 'All' : rt === 'print_time_waiver' ? 'Waivers' : 'Removals'}
                </button>
              ))}
            </div>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="sc-audit-table">
                <thead className="bg-black/30 text-zinc-500 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-2.5 cursor-pointer select-none" onClick={() => toggleSort('timestamp')}>
                      Time {auditSort.key === 'timestamp' && (auditSort.dir === 'asc' ? <CaretUp size={8} className="inline" /> : <CaretDown size={8} className="inline" />)}
                    </th>
                    <th className="text-left px-3 py-2.5">Bill</th>
                    <th className="text-left px-3 py-2.5">Server</th>
                    <th className="text-right px-3 py-2.5 cursor-pointer select-none" onClick={() => toggleSort('final_total')}>
                      Final {auditSort.key === 'final_total' && (auditSort.dir === 'asc' ? <CaretUp size={8} className="inline" /> : <CaretDown size={8} className="inline" />)}
                    </th>
                    <th className="text-right px-3 py-2.5">Base</th>
                    <th className="text-right px-3 py-2.5">Rate</th>
                    <th className="text-right px-3 py-2.5 cursor-pointer select-none" onClick={() => toggleSort('amount')}>
                      Amount {auditSort.key === 'amount' && (auditSort.dir === 'asc' ? <CaretUp size={8} className="inline" /> : <CaretDown size={8} className="inline" />)}
                    </th>
                    <th className="text-left px-5 py-2.5">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAudit.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-5 py-8 text-center text-[13px] text-zinc-500">
                        No audit events for this filter
                      </td>
                    </tr>
                  ) : (
                    filteredAudit.map((r) => (
                      <tr
                        key={r.order_id}
                        className="border-t border-white/5 hover:bg-white/[0.03] transition-colors"
                        data-testid={`sc-audit-row-${r.order_id}`}
                      >
                        <td className="px-5 py-2.5 text-zinc-400 font-mono">{r.business_date} {fmtTime(r.timestamp)}</td>
                        <td className="px-3 py-2.5 text-zinc-300 font-mono">#{r.order_id}</td>
                        <td className="px-3 py-2.5 text-zinc-300 truncate max-w-[180px]">{r.server_name || <span className="text-zinc-600">—</span>}</td>
                        <td className="px-3 py-2.5 text-right text-zinc-300 font-mono">₹{fmtCurrency(r.final_total)}</td>
                        <td className="px-3 py-2.5 text-right text-zinc-500 font-mono">₹{fmtCurrency(r.service_charge_base)}</td>
                        <td className="px-3 py-2.5 text-right text-zinc-500 font-mono">
                          {r.service_charge_rate_used != null ? `${(r.service_charge_rate_used * 100).toFixed(0)}%` : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-amber-300 font-mono">₹{fmtCurrency(r.amount)}</td>
                        <td className="px-5 py-2.5"><ReasonPill type={r.reason_type} /></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceChargePage;
